import React, { useState, useEffect, Suspense } from 'react';
import { Route, Routes } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';

/* Libraries */
import { LicenseInfo } from '@mui/x-license-pro';
import {toast, ToastContainer} from 'react-toastify';
import dayjs from 'dayjs';
import { jwtDecode } from 'jwt-decode'

/* Components */
import Navbar from "./components/Header/Navbar";
import Login from "./components/Login/Login";
import Notifications from "./components/Notifications/Notifications";

/* Services */
import {
	currentUser,
	fetchPortfolios,
	getStatus,
	userLogout,
	fetchOrganizationUsers,
	fetchOrganizationDocuments,
	userOrganization
} from "./services/GraphService";

/* Actions */
import {
	changeUserInfo,
	changePortfoliosData,
	changeChosenPortfolioData,
	changeStatusInfo,
	changeOrganizationUsers,
	changeOrganizationDocuments,
	changePasswordExpirationError,
	changeRenderType
} from './store/actions';

/* Routes */
import routes from './routes/routes';

/* Styles */
import './App.css';
import {render} from "redux-logger/src/diff";

/* Set license key for all data-grid-pro tables */
LicenseInfo.setLicenseKey(process.env.REACT_APP_MUI_LICENSE_KEY);

/* Store */
const mapState = (reducer) => ({
    chosenPortfolioId: reducer?.chosenPortfolio?.portfolio_id,
	orgId: reducer?.user?.organization_id,
	renderType: reducer?.renderType
});

/* Fetch status */
export const fetchStatus = async (organizationId, portfolioId) => {
	if (organizationId && portfolioId) {
		try {
			return await getStatus(organizationId, portfolioId);
		} catch (error) {
			console.error("Error fetching status:", error);
		}
	}
}

const App = () => {
	const dispatch = useDispatch();
	const { chosenPortfolioId, orgId, renderType } = useSelector(mapState);
	const [momentTime, setMomentTime] = useState(null);
	const [userSessionExpiring, setUserSessionExpiring] = useState(false);

	const [showChangePasswordForm, setShowChangePasswordForm] = useState(false);
	const [daysLeftForPasswordExpiration, setDaysLeftForPasswordExpiration] = useState(0);

	/* Fetch data */
	useEffect(() => {
		if (sessionStorage?.token) {
			currentUser()
				.then(result => {
					if (result) {
						const orgId = result?.organization_id;

						userOrganization()
							.then(res => {
								const organizations = res.organizations;

								if (organizations?.length) {
									dispatch(changeUserInfo({
										organizations: organizations
									}));

									if (orgId) {
										const organizationFeatureContainIntraday = organizations?.find((org) => org?.organization_id === orgId);
										const organizationFeatures = organizationFeatureContainIntraday?.features;

										if (organizationFeatures?.includes('INTRADAY_TRADING')) {
											fetchPortfolios(orgId)
												.then(result => {
													const portfoliosResult = result?.portfolios;
													let firstPortfolio = {};
													let portfolioId = firstPortfolio?.portfolio_id;

													if (portfoliosResult?.length) {
														firstPortfolio = portfoliosResult[0];
														portfolioId = firstPortfolio?.portfolio_id;

														fetchStatus(orgId, portfolioId)
															.then(res => {
																if (res) {
																	dispatch(changeStatusInfo(res));
																}
															})
															.catch(error => {
																console.error("Error:", error);
															});
													}

													dispatch(changePortfoliosData(portfoliosResult));
													dispatch(changeChosenPortfolioData(firstPortfolio));

													fetchOrganizationUsers(orgId)
														.then(res => {
															const users = res?.users;

															if (users?.length) {
																dispatch(changeOrganizationUsers(users));
															}
														})
														.catch(error => {
															console.error("Error:", error);
														});

													fetchOrganizationDocuments(orgId)
														.then(result => {
															if (result) {
																dispatch(changeOrganizationDocuments(result));
															}
														})
														.catch(error => {
															console.error("Error:", error);
														});
												})
												.catch(error => {
													console.error("Error:", error);
												});
										}

										let type = 'DocumentTable';

										if(organizationFeatures?.includes('INTRADAY_TRADING')) {
											type = 'intraday';
										} else if (organizationFeatures?.includes('DAY_AHEAD_TRADING')) {
											type = 'dayAhead';
										} else if (organizationFeatures?.includes('USER_REPORTS')) {
											type = 'userReports'
										}

										dispatch(changeRenderType(type));
									}
								}
							})
							.catch(error => {
								console.error("Error:", error);
							});

						dispatch(changeUserInfo(result));
					}
				})
				.catch(error => {
					console.error("Error:", error);
				});

			if (sessionStorage?.passwordExpiration) {
				const today = dayjs();
				const dateExpire = dayjs(sessionStorage?.passwordExpiration);
				const daysDiff = dateExpire?.diff(today, 'day');

				if (daysDiff < 30) {
					dispatch(changePasswordExpirationError(true));
				}

				setShowChangePasswordForm(true);
				setDaysLeftForPasswordExpiration(daysDiff);
			}

			const interval =  setInterval(() => {
				if (momentTime !== dayjs()) {
					setMomentTime(dayjs());
				}
			}, 1000);

			return () => clearInterval(interval);
		}
	}, []);

	useEffect(() => {
		if (sessionStorage?.token) {
			let sessionExp = false;

			const tokenParsed = jwtDecode(sessionStorage?.token);

			const epochTimeInProperFormat = tokenParsed?.exp * 1000;

			const currentDateTime = dayjs();
			const tokenExpirationTime = {
				time: dayjs(epochTimeInProperFormat)
			};

			const timeDiffInHours = tokenExpirationTime?.time.diff(currentDateTime, 'hour');
			const timeDiffInMinutes = tokenExpirationTime?.time.diff(currentDateTime, 'minute');
			const timeDiffInSeconds = tokenExpirationTime?.time.diff(currentDateTime, 'second');

			if (timeDiffInMinutes < 10 && timeDiffInHours === 0) {
				sessionExp = true;

				if (timeDiffInMinutes === 0 && timeDiffInSeconds === 0) {
					userLogout().then(r => {
						sessionStorage.removeItem('token');
						window.location.reload();
					});
				}
			}

			setUserSessionExpiring(sessionExp);
		}
	}, [momentTime]);

	/* Fetch trading chart data every 30 seconds */
	useEffect(() => {
		if (!chosenPortfolioId || !orgId || renderType !== 'intraday') { return; }

		const intervalId = setInterval(() => {
			fetchStatus(orgId, chosenPortfolioId)
				.then(result => {
					if (result) {
						dispatch(changeStatusInfo(result));
					}
				})
				.catch(error => {
					console.error("Error:", error);
				});
		}, 30000); // 30 000 ms = 30 seconds

		return () => {
			clearInterval(intervalId);
		};
	}, [chosenPortfolioId, renderType, orgId]);

	return (
		<React.Fragment>
			<ToastContainer />

			{!sessionStorage.token
				? <Login />
				: <>
					<Navbar showChangePasswordForm={showChangePasswordForm} daysLeftForPasswordExpiration={daysLeftForPasswordExpiration}/>

					<section className='content'>
						<Suspense fallback={<h1>Loading page...</h1>}>
							<Routes>
								{routes.map(({ path, component }) => {
									return (
										<Route exact key={path} path={path} element={component} />
									)
								})}
							</Routes>
						</Suspense>
					</section>

					<Notifications sessionExpire={userSessionExpiring} daysLeftForPasswordExpiration={daysLeftForPasswordExpiration} />
				</>
			}
		</React.Fragment>
	);
}

export default App;
